import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Typography, Stack, Checkbox, LinearProgress } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import axios from 'axios';
import { CHECKLIST } from './data';
import { API_BASE_URL } from '../utils';
import { ORANGE, ORANGE_DK, ORANGE_SOFT, GREEN, RED, TXT, TXT_DIM, card, gradientText, primaryBtn, focusRing } from './theme';

export default function ComplianceChecklist() {
  const navigate = useNavigate();
  const [checked, setChecked] = useState({});
  // Prefer the admin-managed checklist; fall back to the built-in list.
  const [data, setData] = useState(CHECKLIST);

  useEffect(() => {
    let active = true;
    axios.get(`${API_BASE_URL}/api/checklist`)
      .then((res) => { if (active && Array.isArray(res.data?.checklist) && res.data.checklist.length) setData(res.data.checklist); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const total = useMemo(() => data.reduce((n, g) => n + g.items.length, 0), [data]);
  const done = Object.values(checked).filter(Boolean).length;
  const pct = Math.round((done / total) * 100);

  const toggle = (key) => setChecked((c) => ({ ...c, [key]: !c[key] }));
  const reset = () => setChecked({});

  const band =
    pct === 100
      ? { label: 'Audit-ready', desc: 'You’re covering the essentials. Lock it in with a system of record.', color: GREEN }
      : pct >= 70
      ? { label: 'Almost there', desc: 'A few items left — tighten these to be Board-ready.', color: ORANGE_DK }
      : pct >= 40
      ? { label: 'On your way', desc: 'Solid start, but key gaps could expose you in an audit.', color: '#B45309' }
      : { label: 'Significant gaps', desc: 'Several fundamentals are missing — worth acting on soon.', color: RED };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1.4fr 1fr' },
        gap: { xs: 3, md: 4 },
        alignItems: 'start',
      }}
    >
      {/* checklist */}
      <Box sx={{ ...card, p: { xs: 2.5, md: 4 } }}>
        <Stack spacing={3.5}>
          {data.map((group, ci) => (
            <Box key={group.category}>
              <Typography sx={{ color: ORANGE_DK, fontWeight: 700, letterSpacing: 1.2, fontSize: '0.72rem', mb: 1.5 }}>
                {group.category.toUpperCase()}
              </Typography>
              <Stack spacing={0.5}>
                {group.items.map((item, ii) => {
                  const key = `${ci}-${ii}`;
                  const on = !!checked[key];
                  return (
                    <Stack
                      key={key}
                      direction="row"
                      alignItems="center"
                      onClick={() => toggle(key)}
                      sx={{
                        p: 1,
                        borderRadius: 1.5,
                        cursor: 'pointer',
                        transition: 'background .2s',
                        '&:hover': { background: ORANGE_SOFT },
                      }}
                    >
                      <Checkbox
                        checked={on}
                        disableRipple
                        inputProps={{ 'aria-label': item }}
                        sx={{ p: 0.5, mr: 1, color: '#94A3B8', '&.Mui-checked': { color: ORANGE } }}
                      />
                      <Typography
                        sx={{
                          fontSize: '0.92rem',
                          color: on ? TXT_DIM : TXT,
                          textDecoration: on ? 'line-through' : 'none',
                          transition: 'color .2s',
                        }}
                      >
                        {item}
                      </Typography>
                    </Stack>
                  );
                })}
              </Stack>
            </Box>
          ))}
        </Stack>
      </Box>

      {/* live score */}
      <Box sx={{ ...card, p: { xs: 3, md: 4 }, position: { md: 'sticky' }, top: { md: 96 } }}>
        <Typography sx={{ color: TXT_DIM, fontWeight: 600, fontSize: '0.8rem', letterSpacing: 0.5, mb: 1 }}>
          YOUR READINESS SCORE
        </Typography>
        <Typography sx={{ fontWeight: 800, fontSize: '3.4rem', lineHeight: 1, ...gradientText }}>{pct}%</Typography>
        <Typography sx={{ color: TXT_DIM, fontSize: '0.85rem', mt: 0.5, mb: 2 }}>
          {done} of {total} checks complete
        </Typography>

        <LinearProgress
          variant="determinate"
          value={pct}
          aria-label="DPDP Act readiness score"
          sx={{
            height: 8,
            borderRadius: 999,
            background: 'rgba(15,23,42,0.06)',
            '& .MuiLinearProgress-bar': { borderRadius: 999, background: band.color, transition: 'transform .4s' },
          }}
        />

        <Box
          sx={{
            mt: 2.5,
            p: 2,
            borderRadius: 2,
            background: `${band.color}12`,
            border: `1px solid ${band.color}33`,
          }}
        >
          <Typography sx={{ fontWeight: 700, color: band.color, fontSize: '0.95rem', mb: 0.3 }}>{band.label}</Typography>
          <Typography sx={{ color: TXT_DIM, fontSize: '0.85rem', lineHeight: 1.5 }}>{band.desc}</Typography>
        </Box>

        <Button fullWidth sx={{ ...primaryBtn, mt: 2.5 }} endIcon={<ArrowForwardIcon />} onClick={() => navigate('/register')}>
          Close the gaps with CMS
        </Button>
        <Button
          fullWidth
          onClick={reset}
          sx={{ mt: 1, color: TXT_DIM, fontWeight: 600, textTransform: 'none', ...focusRing, '&:hover': { color: TXT, background: 'transparent' } }}
        >
          Reset checklist
        </Button>

        <Typography sx={{ color: TXT_DIM, fontSize: '0.72rem', mt: 2, lineHeight: 1.5, textAlign: 'center' }}>
          This self-assessment is a general readiness guide, not legal advice.
        </Typography>
      </Box>
    </Box>
  );
}
